/**
 * Facebook Pixel Utility
 * Centralized tracking with deduplication safeguards
 * 
 * This utility ensures:
 * - Pixel is only initialized once
 * - Events are not duplicated
 * - Event IDs are consistent between client and server
 */

const PIXEL_ID = '1448455159507881';
const trackedEvents = new Set(); // Track event IDs to prevent duplicates

/**
 * Build Meta Pixel Advanced Matching payload (plain values — Pixel hashes them).
 * Keys: em, ph, fn, ln, ct, external_id (Meta naming).
 */
export function buildAdvancedMatching({
  email,
  phone,
  firstName,
  lastName,
  fullName,
  city,
  externalId,
} = {}) {
  const data = {};

  const em = email && String(email).trim().toLowerCase();
  if (em && /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(em)) {
    data.em = em;
  }

  if (phone) {
    let digits = String(phone).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('03')) {
      digits = '92' + digits.substring(1);
    } else if (digits.length === 10 && !digits.startsWith('0')) {
      digits = '92' + digits;
    }
    if (digits) data.ph = digits;
  }

  let fn = firstName && String(firstName).trim();
  let ln = lastName && String(lastName).trim();
  if ((!fn || !ln) && fullName) {
    const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) fn = fn || parts[0];
    else if (parts.length > 1) {
      fn = fn || parts[0];
      ln = ln || parts.slice(1).join(' ');
    }
  }
  if (fn) data.fn = fn.toLowerCase();
  if (ln) data.ln = ln.toLowerCase();

  if (city && String(city).trim()) {
    data.ct = String(city).trim().toLowerCase();
  }

  if (externalId) {
    data.external_id = String(externalId);
  }

  return data;
}

/**
 * Initialize Facebook Pixel (should only be called once for script load).
 * Pass Advanced Matching as the 3rd fbq('init') arg when known.
 */
export function initFacebookPixel(advancedMatching = {}) {
  // Use window-level flag to persist across module reloads (React StrictMode in dev)
  if (window._fbPixelInitialized) {
    console.warn('[Meta Pixel] Already initialized, skipping');
    return;
  }

  // Set flag immediately at the start to prevent race conditions
  // This prevents double initialization even if React StrictMode calls this twice
  window._fbPixelInitialized = true;

  // Load the pixel script
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;
    n=f.fbq=function(){
      n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)
    };
    if(!f._fbq)f._fbq=n;
    n.push=n;
    n.loaded=!0;
    n.version='2.0';
    n.queue=[];
    t=b.createElement(e);
    t.async=!0;
    t.src=v;
    s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');

  // Initialize pixel
  if (Object.keys(advancedMatching).length > 0) {
    window.fbq('init', PIXEL_ID, advancedMatching);
    console.info('[Meta Pixel] Initialized with advanced matching', Object.keys(advancedMatching));
  } else {
    window.fbq('init', PIXEL_ID);
    console.info('[Meta Pixel] Initialized without advanced matching');
  }
}

/**
 * Meta allows calling fbq('init', id, userData) again after first init
 * to attach Advanced Matching once phone/email become available (e.g. checkout).
 */
export function updateFacebookAdvancedMatching(contact = {}) {
  if (typeof window === 'undefined' || !window.fbq) return;

  const advancedMatching =
    contact.em || contact.ph
      ? contact
      : buildAdvancedMatching(contact);

  if (!Object.keys(advancedMatching).length) return;

  window.fbq('init', PIXEL_ID, advancedMatching);
  console.info('[Meta Pixel] Advanced matching updated', Object.keys(advancedMatching));
}

/**
 * Track PageView event (with deduplication for SPA route changes)
 */
export function trackPageView(path = window.location.pathname) {
  if (!window.fbq) {
    console.warn('[Meta Pixel] Pixel not initialized, cannot track PageView');
    return;
  }

  // Create a unique key for this PageView (path + timestamp within 1 second)
  const now = Math.floor(Date.now() / 1000);
  const eventKey = `pageview-${path}-${now}`;

  // Prevent duplicate PageViews within the same second
  if (trackedEvents.has(eventKey)) {
    console.warn('[Meta Pixel] Duplicate PageView prevented:', path);
    return;
  }

  window.fbq('track', 'PageView');
  trackedEvents.add(eventKey);

  // Clean up old tracked events (keep only last 100)
  if (trackedEvents.size > 100) {
    const entries = Array.from(trackedEvents);
    entries.slice(0, entries.length - 100).forEach(key => trackedEvents.delete(key));
  }

  console.info('[Meta Pixel] PageView tracked:', path);
}

/**
 * Track AddToCart event (with deduplication)
 */
export function trackAddToCart(data, eventId = null) {
  if (!window.fbq) {
    console.warn('[Meta Pixel] Pixel not initialized, cannot track AddToCart');
    return;
  }

  // Generate event ID if not provided
  const finalEventId = eventId || `addtocart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Prevent duplicate events with same ID
  if (eventId && trackedEvents.has(`addtocart-${eventId}`)) {
    console.warn('[Meta Pixel] Duplicate AddToCart prevented:', eventId);
    return;
  }

  const payload = {
    value: data.value || 0,
    currency: data.currency || 'PKR',
    content_ids: data.content_ids,
    content_type: data.content_type || 'product',
    num_items: data.num_items,
    ...data
  };

  window.fbq('track', 'AddToCart', payload, { eventID: finalEventId });
  trackedEvents.add(`addtocart-${finalEventId}`);

  console.info('[Meta Pixel] AddToCart tracked:', payload);
}

/**
 * Track Purchase event (with deduplication)
 * IMPORTANT: eventId must match the server-side event ID format
 */
export function trackPurchase(data, eventId) {
  if (!window.fbq) {
    console.warn('[Meta Pixel] Pixel not initialized, cannot track Purchase');
    return;
  }

  if (!eventId) {
    console.error('[Meta Pixel] Purchase event requires eventId for deduplication');
    return;
  }

  // Prevent duplicate Purchase events
  if (trackedEvents.has(`purchase-${eventId}`)) {
    console.warn('[Meta Pixel] Duplicate Purchase prevented:', eventId);
    return;
  }

  const payload = {
    value: data.value || 0,
    currency: data.currency || 'PKR',
    content_ids: data.content_ids,
    content_type: data.content_type || 'product',
    num_items: data.num_items,
    ...data
  };

  // Use eventID parameter (not eventId) for Meta Pixel
  window.fbq('track', 'Purchase', payload, { eventID: eventId });
  trackedEvents.add(`purchase-${eventId}`);

  console.info('[Meta Pixel] Purchase tracked:', { eventId, payload });
}

/**
 * Track ViewContent event (for product pages)
 */
export function trackViewContent(data, eventId = null) {
  if (!window.fbq) {
    console.warn('[Meta Pixel] Pixel not initialized, cannot track ViewContent');
    return;
  }

  // Generate event ID if not provided
  const finalEventId = eventId || `viewcontent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const payload = {
    content_ids: data.content_ids || [],
    content_type: data.content_type || 'product',
    content_name: data.content_name,
    value: data.value || 0,
    currency: data.currency || 'PKR',
    ...data
  };

  window.fbq('track', 'ViewContent', payload, { eventID: finalEventId });
  console.info('[Meta Pixel] ViewContent tracked:', payload);
}

/**
 * Track custom event
 */
export function trackEvent(eventName, data = {}, eventId = null) {
  if (!window.fbq) {
    console.warn('[Meta Pixel] Pixel not initialized, cannot track event:', eventName);
    return;
  }

  const options = eventId ? { eventID: eventId } : {};
  window.fbq('track', eventName, data, options);
  console.info('[Meta Pixel] Event tracked:', eventName, data);
}

/**
 * Check if pixel is initialized
 */
export function isPixelInitialized() {
  return window._fbPixelInitialized && !!window.fbq;
}

